<template>
    <div ref="selector" :class="['tabs-selector', controlThemeClass]">
        <span
            v-if="showActiveBubble"
            class="active-bubble"
            :style="activeBubbleStyle"
        />
        <button
            v-for="(tab, index) in tabs"
            :key="tab.tabId || index"
            :ref="(el) => setTabRef(el, index)"
            :class="['tab-pill', { active: activeTabIndex === index }]"
            @click="handlePillClick(index)"
        >
            <span
                :ref="(el) => setTitleRef(el, index)"
                class="tab-pill-title"
                :contenteditable="isTitleEditing(index)"
                @beforeinput="onTitleBeforeInput"
                @blur="leaveEditMode(index)"
                @click="handleTitleClick($event, index)"
                @input="onTitleInput"
                @keydown="onTitleKeyDown($event, index)"
                @paste="onTitlePaste"
            >
                {{ tab.title || defaultTitle(index) }}
            </span>
        </button>
    </div>
</template>

<script>
import {getControlThemeClass} from "@/scripts/common/utilities/colorUtils.js";
import {mapGetters} from "vuex";

const MAX_TAB_TITLE_LENGTH = 24;

export default {
    name: "TabsSelector",
    emits: ['tab-change', 'tab-title-change'],
    props: {
        tabs: {
            type: Array,
            default: () => []
        },
        sectionBgColor: {
            type: String,
            default: null
        },
        editable: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            activeTabIndex: 0,
            activeBubble: null,
            resizeObserver: null,
            tabRefs: [],
            titleRefs: [],
            editingIndex: null
        }
    },
    computed: {
        ...mapGetters("design", ["colorPalette", "resolveColor"]),
        controlThemeClass() {
            return getControlThemeClass(
                this.sectionBgColor || this.resolveColor("background"),
                this.colorPalette
            );
        },
        showActiveBubble() {
            return !!this.activeBubble;
        },
        activeBubbleStyle() {
            if (!this.activeBubble) return null;

            return {
                width: `${this.activeBubble.width}px`,
                height: `${this.activeBubble.height}px`,
                transform: `translate(${this.activeBubble.left}px, ${this.activeBubble.top}px)`
            };
        }
    },
    watch: {
        tabs: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this.activeTabIndex = Math.min(this.activeTabIndex, Math.max(this.tabs.length - 1, 0));
                    this.observeTabs();
                    this.updateActiveBubble();
                });
            }
        },
        activeTabIndex() {
            this.$nextTick(this.updateActiveBubble);
        }
    },
    mounted() {
        this.observeTabs();
        this.$nextTick(this.updateActiveBubble);
        window.addEventListener('resize', this.updateActiveBubble);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateActiveBubble);
        this.disconnectResizeObserver();
    },
    methods: {
        defaultTitle(index) {
            return `Tab ${index + 1}`;
        },
        isTitleEditing(index) {
            return this.editable && this.editingIndex === index;
        },
        setTabRef(el, index) {
            if (el) {
                this.tabRefs[index] = el;
            } else {
                this.tabRefs[index] = null;
            }
        },
        setTitleRef(el, index) {
            if (el) {
                this.titleRefs[index] = el;
            } else {
                this.titleRefs[index] = null;
            }
        },
        handlePillClick(index) {
            if (this.editable && this.activeTabIndex === index) {
                this.enterEditMode(index);
                return;
            }
            this.activeTabIndex = index;
            this.$emit('tab-change', index);
        },
        handleTitleClick(event, index) {
            if (!this.isTitleEditing(index)) return;
            event.stopPropagation();
        },
        enterEditMode(index) {
            if (this.editingIndex === index) return;
            this.editingIndex = index;
            this.$nextTick(() => {
                const title = this.titleRefs[index];
                if (!title) return;
                title.focus();
                this.placeCursorAtEnd(title);
            });
        },
        leaveEditMode(index) {
            if (this.editingIndex !== index) return;
            const title = this.titleRefs[index];
            const nextTitle = this.truncateTitle(title?.textContent?.trim()) || this.defaultTitle(index);
            if (title) title.textContent = nextTitle;
            this.editingIndex = null;
            this.$emit('tab-title-change', {index, title: nextTitle});
            this.$nextTick(this.updateActiveBubble);
        },
        onTitleKeyDown(event, index) {
            if (event.code !== "Enter" && event.code !== "Escape") return;
            event.preventDefault();
            this.leaveEditMode(index);
            this.titleRefs[index]?.blur();
        },
        onTitleBeforeInput(event) {
            if (event.inputType === "insertParagraph" || event.inputType === "insertLineBreak") {
                event.preventDefault();
                return;
            }
            if (!event.data) return;

            const currentLength = event.currentTarget?.textContent?.length || 0;
            const selectedLength = this.getSelectedTextLength(event.currentTarget);
            const nextLength = currentLength - selectedLength + event.data.length;
            if (nextLength > MAX_TAB_TITLE_LENGTH) {
                event.preventDefault();
            }
        },
        onTitleInput(event) {
            const title = event.currentTarget;
            if (title?.textContent?.length > MAX_TAB_TITLE_LENGTH) {
                title.textContent = this.truncateTitle(title.textContent);
                this.placeCursorAtEnd(title);
            }
            this.$nextTick(this.updateActiveBubble);
        },
        onTitlePaste(event) {
            event.preventDefault();
            const text = event.clipboardData?.getData("text/plain")?.replace(/\s+/g, " ") || "";
            const currentLength = event.currentTarget?.textContent?.length || 0;
            const selectedLength = this.getSelectedTextLength(event.currentTarget);
            const remainingLength = Math.max(MAX_TAB_TITLE_LENGTH - currentLength + selectedLength, 0);
            this.insertPlainText(this.truncateTitle(text, remainingLength));
            this.$nextTick(this.updateActiveBubble);
        },
        truncateTitle(title, maxLength = MAX_TAB_TITLE_LENGTH) {
            return (title || "").slice(0, maxLength);
        },
        getSelectedTextLength(container) {
            const selection = window.getSelection();
            if (!selection?.rangeCount || !container?.contains(selection.anchorNode) || !container.contains(selection.focusNode)) {
                return 0;
            }
            return selection.toString().length;
        },
        insertPlainText(text) {
            const selection = window.getSelection();
            if (!selection?.rangeCount) return;

            selection.deleteFromDocument();
            const range = selection.getRangeAt(0);
            const textNode = document.createTextNode(text);
            range.insertNode(textNode);
            range.setStartAfter(textNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        },
        placeCursorAtEnd(el) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
        },
        updateActiveBubble() {
            const activeTab = this.tabRefs[this.activeTabIndex];
            if (!activeTab) {
                this.activeBubble = null;
                return;
            }

            this.activeBubble = {
                left: activeTab.offsetLeft,
                top: activeTab.offsetTop,
                width: activeTab.offsetWidth,
                height: activeTab.offsetHeight
            };
        },
        observeTabs() {
            if (typeof ResizeObserver === 'undefined') return;

            this.disconnectResizeObserver();
            this.resizeObserver = new ResizeObserver(() => {
                this.updateActiveBubble();
            });

            if (this.$refs.selector) {
                this.resizeObserver.observe(this.$refs.selector);
            }

            this.tabRefs.forEach(tabRef => {
                if (tabRef) this.resizeObserver.observe(tabRef);
            });
        },
        disconnectResizeObserver() {
            if (!this.resizeObserver) return;
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }
}
</script>

<style lang="scss" scoped>
    .tabs-selector {
        position: relative;
        width: max-content;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        justify-content: center;
        padding: 4px;
        background-color: var(--UIControlContainerBgColor);
        border-radius: 999px;
        min-height: 50px;
    }

    .active-bubble {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
        border-radius: 999px;
        background-color: var(--UIControlItemFillColor);
        pointer-events: none;
        transition:
            transform 180ms ease,
            width 180ms ease,
            height 180ms ease;
    }

    .tab-pill {
        position: relative;
        z-index: 1;
        border-radius: 999px;
        background: transparent;
        cursor: pointer;
        font: inherit;
        padding: 10px 22px;
        outline: none;
        border: none;
        font-family: var(--section-p1-font),serif;
        font-size: 16px;
        color: var(--paletteColor1);

        .tab-pill-title {
            display: block;
            outline: none;
            white-space: nowrap;
        }
    }
</style>
